import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FormField({ field, value, onChange, error }) {
    const { field_type, label, required, placeholder, options } = field;
    const baseInput = "w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors";
    const inputClass = `${baseInput} ${error ? 'border-red-300 focus:ring-red-400' : 'border-stone-200 focus:ring-stone-400'}`;

    if (field_type === 'textarea') {
        return <textarea value={value || ''} onChange={e => onChange(e.target.value)}
            rows={3} className={inputClass} placeholder={placeholder} />;
    }
    if (field_type === 'select') {
        return (
            <select value={value || ''} onChange={e => onChange(e.target.value)} className={inputClass}>
                <option value="">{placeholder || 'Select...'}</option>
                {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        );
    }
    if (field_type === 'multi_select') {
        const selected = value || [];
        return (
            <div className="space-y-1">
                {(options || []).map(o => (
                    <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={selected.includes(o)}
                            onChange={e => {
                                if (e.target.checked) onChange([...selected, o]);
                                else onChange(selected.filter(s => s !== o));
                            }}
                            className="rounded border-stone-300" />
                        {o}
                    </label>
                ))}
            </div>
        );
    }
    if (field_type === 'checkbox') {
        return (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
                    className="rounded border-stone-300" />
                {placeholder || label}
            </label>
        );
    }

    const typeMap = { email: 'email', phone: 'tel', number: 'number', date: 'date', url: 'url' };
    return <input type={typeMap[field_type] || 'text'} value={value || ''}
        onChange={e => onChange(e.target.value)} className={inputClass} placeholder={placeholder} />;
}

export default function PublicForm() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const isEmbed = searchParams.get('embed') === 'true';

    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [answers, setAnswers] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState('');

    useEffect(() => {
        axios.get(`${API}/forms/public/${slug}`)
            .then(res => {
                setFormData(res.data);
                // Initialize defaults
                const defaults = {};
                (res.data.fields || []).forEach(f => {
                    if (f.default_value !== null && f.default_value !== undefined) {
                        defaults[f.field_id] = f.default_value;
                    }
                });
                setAnswers(defaults);
            })
            .catch(err => setError(err.response?.data?.detail || 'Form not found'))
            .finally(() => setLoading(false));
    }, [slug]);

    // Auto-resize for embed
    useEffect(() => {
        if (isEmbed) {
            const sendHeight = () => {
                window.parent.postMessage({
                    type: 'nurture-form-resize', slug,
                    height: document.body.scrollHeight,
                }, '*');
            };
            sendHeight();
            const observer = new ResizeObserver(sendHeight);
            observer.observe(document.body);
            return () => observer.disconnect();
        }
    }, [isEmbed, slug, submitted]);

    const validate = () => {
        const errors = {};
        (formData?.fields || []).forEach(f => {
            if (f.required) {
                const val = answers[f.field_id];
                if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
                    errors[f.field_id] = 'Required';
                }
            }
            // Basic email validation
            if (f.field_type === 'email' && answers[f.field_id]) {
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers[f.field_id])) {
                    errors[f.field_id] = 'Invalid email';
                }
            }
        });
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/forms/public/${slug}`, {
                answers,
                metadata: {
                    referrer: document.referrer,
                    user_agent: navigator.userAgent,
                    embed: isEmbed,
                },
            });

            if (res.data.redirect_url) {
                const newTab = res.data.redirect_new_tab !== false; // default true
                if (newTab) {
                    window.open(res.data.redirect_url, '_blank', 'noopener,noreferrer');
                } else {
                    window.location.href = res.data.redirect_url;
                }
            }

            setConfirmMessage(res.data.confirmation_message || 'Thank you!');
            setSubmitted(true);
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail) {
                alert(detail);
            } else {
                alert('Something went wrong. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const brandColor = formData?.styling?.brand_color || '#1c1917';

    if (loading) {
        return (
            <div className={`flex items-center justify-center ${isEmbed ? 'min-h-[200px]' : 'min-h-screen'} bg-[#FAFAF5]`}>
                <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className={`flex flex-col items-center justify-center ${isEmbed ? 'min-h-[200px]' : 'min-h-screen'} bg-[#FAFAF5]`}>
                <AlertCircle className="w-10 h-10 text-stone-300 mb-2" />
                <p className="text-stone-500">{error}</p>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className={`flex flex-col items-center justify-center ${isEmbed ? 'min-h-[200px] p-6' : 'min-h-screen'} bg-[#FAFAF5]`}>
                <div className="bg-white rounded-xl border border-stone-200 p-8 max-w-md text-center shadow-sm">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: brandColor }} />
                    <h2 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Submitted!</h2>
                    <p className="text-sm text-stone-600">{confirmMessage}</p>
                </div>
                {formData?.styling?.show_branding !== false && (
                    <p className="text-[10px] text-stone-400 mt-4">
                        Powered by <a href="https://nurturecrm.ai" target="_blank" rel="noopener noreferrer" className="underline">NurtureCRM</a>
                    </p>
                )}
            </div>
        );
    }

    const fields = (formData?.fields || []).sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
        <div className={`${isEmbed ? 'p-4' : 'min-h-screen flex items-start justify-center py-12 px-4'} bg-[#FAFAF5]`}>
            <div className="w-full max-w-lg">
                <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
                    {formData.show_form_title !== false && (
                        <h1 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {formData.form_title || formData.name}
                        </h1>
                    )}
                    {formData.description && (
                        <p className="text-sm text-stone-500 mb-5">{formData.description}</p>
                    )}

                    <div className="space-y-4">
                        {fields.map(field => (
                            <div key={field.field_id}>
                                {field.field_type !== 'checkbox' && (
                                    <label className="block text-sm font-medium text-stone-700 mb-1">
                                        {field.label} {field.required && <span className="text-red-500">*</span>}
                                    </label>
                                )}
                                <FormField field={field} value={answers[field.field_id]}
                                    onChange={val => setAnswers(a => ({ ...a, [field.field_id]: val }))}
                                    error={fieldErrors[field.field_id]} />
                                {fieldErrors[field.field_id] && (
                                    <p className="text-xs text-red-500 mt-0.5">{fieldErrors[field.field_id]}</p>
                                )}
                            </div>
                        ))}
                    </div>

                    <button type="submit" disabled={submitting}
                        className="mt-6 w-full py-2.5 rounded-lg text-white font-medium text-sm transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                        style={{ backgroundColor: brandColor }}>
                        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {submitting ? 'Submitting...' : (formData?.styling?.button_text || 'Submit')}
                    </button>
                </form>

                {formData?.styling?.show_branding !== false && (
                    <p className="text-center text-[10px] text-stone-400 mt-4">
                        Powered by <a href="https://nurturecrm.ai" target="_blank" rel="noopener noreferrer" className="underline">NurtureCRM</a>
                    </p>
                )}
            </div>
        </div>
    );
}
